/* We Are Jolies B2B — THE NAVIGATION (CD S36 36b « le rail » · S37 37a desktop · 37c mobile)
 *
 * ONE component, two layouts (build note 3). Desktop: a trigger opens a 1200px panel whose left
 * rail owns the right pane. Mobile: a full-screen panel under the 56px bar, level 1 included, the
 * categories as an inline accordion.
 *
 * A CUSTOM build, sanctioned in S36 — this does not extend Focal's mega-menu behaviour, and the
 * native `desktop-menu` snippet is not rendered in B2B mode at all.
 *
 * ⚠️ DERIVED: CD draws the panel's STATES, never its trigger gesture. Desktop opens on hover AND on
 * focus/click, closes on pointer-leave, on Esc and on focus leaving the item — the smallest
 * inference that keeps the panel reachable by keyboard, which hover alone never is. Recorded in
 * DECISIONS.md rather than left implicit.
 */
(function () {
  'use strict';

  var DESKTOP = '(min-width: 1000px)';

  /* ── DESKTOP: the rail panel ───────────────────────────────────────────────────────────────── */
  function initDesktop(nav) {
    var items = Array.prototype.slice.call(nav.querySelectorAll('[data-b2bnav-item]'));
    if (!items.length) return;

    function close(item) {
      var trigger = item.querySelector('[data-b2bnav-trigger]');
      var panel = item.querySelector('[data-b2bnav-panel]');
      if (!trigger || !panel) return;
      trigger.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
    }

    function open(item) {
      if (item._navCancelClose) item._navCancelClose();
      items.forEach(function (other) { if (other !== item) close(other); });
      var trigger = item.querySelector('[data-b2bnav-trigger]');
      var panel = item.querySelector('[data-b2bnav-panel]');
      if (!trigger || !panel) return;
      trigger.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
    }

    items.forEach(function (item) {
      var trigger = item.querySelector('[data-b2bnav-trigger]');
      var panel = item.querySelector('[data-b2bnav-panel]');
      if (!trigger || !panel) return;

      // 🔴 A DELAYED CLOSE, cancelled on re-enter. The CSS bridge (b2b-nav.css) covers the straight
      // path from the link down into the panel; it cannot cover the DIAGONAL one, where the pointer
      // crosses a neighbouring item's column on the way and genuinely leaves this item. 160ms is
      // long enough for that traverse and short enough that an intentional leave still reads as
      // instant. Focal's native dropdown behaves the same way.
      var closeTimer = null;
      function cancelClose() { if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; } }
      function closeSoon() {
        cancelClose();
        closeTimer = setTimeout(function () { close(item); closeTimer = null; }, 160);
      }
      item._navCancelClose = cancelClose;

      item.addEventListener('mouseenter', function () {
        cancelClose();
        if (window.matchMedia(DESKTOP).matches) open(item);
      });
      item.addEventListener('mouseleave', closeSoon);
      trigger.addEventListener('click', function () {
        if (trigger.getAttribute('aria-expanded') === 'true') close(item); else open(item);
      });
      // Keyboard: the panel has to be reachable without a pointer. Opening on focus-in and closing
      // on focus-out gives Tab the same access hover gives a mouse, with no extra affordance drawn.
      item.addEventListener('focusin', function () { open(item); });
      item.addEventListener('focusout', function (event) {
        if (!item.contains(event.relatedTarget)) close(item);
      });

      // ── The rail owns the pane. 37a: « le rail à gauche, le volet appartient à la catégorie
      // active ». Switching on POINTERENTER as well as click is what makes the rail read as a rail
      // rather than as a list of buttons — the pane follows the finger the way it follows the eye.
      var rails = Array.prototype.slice.call(item.querySelectorAll('[data-b2bnav-rail]'));
      var panes = Array.prototype.slice.call(item.querySelectorAll('[data-b2bnav-pane]'));

      function activate(index) {
        rails.forEach(function (r) {
          var on = r.getAttribute('data-b2bnav-rail') === String(index);
          r.classList.toggle('is-active', on);
          // `aria-current`, not `aria-selected`: the rail items became LINKS on 2026-08-30 so the
          // category is clickable, and a link is not a tab. The CSS keys off `.is-active` either way.
          if (on) { r.setAttribute('aria-current', 'true'); } else { r.removeAttribute('aria-current'); }
        });
        panes.forEach(function (p) {
          var on = p.getAttribute('data-b2bnav-pane') === String(index);
          p.hidden = !on;
          p.classList.toggle('is-active', on);
        });
      }

      rails.forEach(function (rail) {
        var index = rail.getAttribute('data-b2bnav-rail');
        rail.addEventListener('mouseenter', function () { activate(index); });
        rail.addEventListener('focus', function () { activate(index); });
        rail.addEventListener('click', function () { activate(index); });
      });

      // ── 37b — the pane image is a STATE, not a fixed illustration.
      // « Au repos : la première photo produit de la catégorie active. Au survol d'un rayon : celle
      // de ce rayon. » Restored on leave from the pane's own default, which is stamped in Liquid —
      // never remembered from the last hover, or leaving one aisle for another would strand the
      // previous aisle's photo under the category.
      //
      // 🔴 2026-08-06 — the caption node was removed (37a: « Prêt-à-porter · 1ᵉʳ produit · 2:3 » is
      // the FRAME'S OWN ANNOTATION, not a drawn element) and this guard still required it. With no
      // [data-b2bnav-cap-out] in the DOM the callback returned before binding a single listener, so
      // the swap died silently on every aisle. The image is the whole feature; the caption never was.
      panes.forEach(function (pane) {
        var media = pane.querySelector('[data-b2bnav-media]');
        if (!media) return;
        var defaultSrc = media.getAttribute('data-b2bnav-img-default') || '';

        function setMedia(src) {
          if (media.tagName === 'IMG' && src) { media.src = src; }
        }

        pane.querySelectorAll('[data-b2bnav-img]').forEach(function (aisle) {
          aisle.addEventListener('mouseenter', function () {
            setMedia(aisle.getAttribute('data-b2bnav-img'));
          });
        });
        pane.addEventListener('mouseleave', function () { setMedia(defaultSrc); });
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') items.forEach(close);
    });
  }

  /* ── MOBILE: the full-screen panel ─────────────────────────────────────────────────────────── */
  function initMobile(panel) {
    // 🔴 THE PANEL HAS TO LIVE ON <body>, and this is Focal's own idiom — its `<mobile-navigation>`
    // carries an `append-body` attribute for exactly this reason.
    //
    // Reported 2026-08-06: « seems to open only from homepage ». Measured on
    // /collections/jolies-culottes at 390: the panel DID open (hidden=false, display:flex) at
    // top 116 with a height of ZERO, against its own --b2bnavm-top of 86px.
    //
    // Cause: `position: fixed` is only viewport-relative while NO ancestor is transformed. Focal's
    // sticky <store-header> transforms itself to hide and re-show on scroll, and a transformed
    // ancestor becomes the containing block for a fixed descendant — so `bottom: 0` resolved to the
    // HEADER's bottom rather than the viewport's, and the panel flattened. The homepage happened to
    // be at a scroll position where the header was untransformed, which is why it looked
    // page-specific rather than scroll-specific.
    //
    // Moving the node to <body> removes the ancestor entirely. It must happen before the first
    // open, so it is the first thing here.
    if (panel.parentElement !== document.body) document.body.appendChild(panel);

    // 37c — the panel is anchored UNDER the bar, and the bar's height is a live number (Focal hides
    // and re-shows the header on scroll, and the announcement bar may or may not be present). So it
    // is measured and published, not assumed at 56.
    var bar = document.querySelector('.b2bh-m__bar');
    function anchor() {
      if (!bar) return;
      var r = bar.getBoundingClientRect();
      panel.style.setProperty('--b2bnavm-top', Math.max(0, Math.round(r.bottom)) + 'px');
    }

    var trigger = document.querySelector('[data-b2bnavm-trigger]');

    function setOpen(on) {
      panel.hidden = !on;
      if (trigger) {
        trigger.setAttribute('aria-expanded', on ? 'true' : 'false');
        trigger.classList.toggle('is-open', on);
      }
      // The panel owns the viewport under the bar, so the page behind it must not scroll with it.
      // `overflow` on the root rather than `position: fixed` on the body: the bar above stays put
      // and nothing about the document's layout changes, so reopening cannot shift the page.
      document.documentElement.style.overflow = on ? 'hidden' : '';
      if (on) anchor();
    }

    if (trigger) {
      trigger.addEventListener('click', function () {
        setOpen(panel.hidden);
      });
    }
    window.addEventListener('resize', function () { if (!panel.hidden) anchor(); });

    // ── THE PUSHED SCREENS (S38 38b's mechanic, operator-ruled 2026-08-06) ───────────────────
    // Replaces 37c's expand-in-place accordion. A category opens its OWN screen, which slides in
    // from the right over level 1 while level 1 slides left underneath — the operator's report was
    // that submenus appearing with no motion at all is « quite destabilising », and the motion is
    // what tells you where you went and how to get back.
    //
    // Both screens are server-rendered and merely translated, so the transition has real nodes to
    // animate and nothing is injected mid-gesture.
    var root = panel.querySelector('[data-b2bnavm-root]');
    var screens = Array.prototype.slice.call(panel.querySelectorAll('[data-b2bnavm-screen]'));

    // 250ms — the collection filter drawer's travel (`ocgf-sheet-in`), which is this build's
    // reference for « comme Focal ». Kept in one place so the CSS and the hidden-attribute timing
    // cannot drift apart.
    var PUSH_MS = 250;
    var pushTimer = null;

    function showScreen(screen) {
      if (pushTimer) { clearTimeout(pushTimer); pushTimer = null; }
      screens.forEach(function (s) { if (s !== screen) { s.hidden = true; s.classList.remove('is-in'); } });
      // `hidden` must come off BEFORE the class, and a layout must be forced between the two, or
      // the element goes from display:none straight to its final transform in one frame and the
      // browser has nothing to interpolate — the change would apply with no visible travel.
      screen.hidden = false;
      screen.getBoundingClientRect();
      screen.classList.add('is-in');
      if (root) root.classList.add('is-pushed');
      screen.scrollTop = 0;
    }

    function popScreen() {
      var open = screens.filter(function (s) { return !s.hidden; })[0];
      if (root) root.classList.remove('is-pushed');
      if (!open) return;
      open.classList.remove('is-in');
      // Re-hide only AFTER the slide-out has finished; hiding immediately would delete the very
      // frames the transition needs.
      pushTimer = setTimeout(function () { open.hidden = true; pushTimer = null; }, PUSH_MS);
    }

    panel.querySelectorAll('[data-b2bnavm-push]').forEach(function (row) {
      row.addEventListener('click', function () {
        var key = row.getAttribute('data-b2bnavm-push');
        var screen = panel.querySelector('[data-b2bnavm-screen="' + key + '"]');
        if (screen) showScreen(screen);
      });
    });

    panel.querySelectorAll('[data-b2bnavm-back]').forEach(function (btn) {
      btn.addEventListener('click', popScreen);
    });

    // Closing the whole menu resets it to level 1 — reopening it inside a category the buyer left
    // three pages ago is not a state anyone asked for. Wrapped rather than folded into setOpen so
    // the reset happens without the slide (the panel is already gone).
    var baseSetOpen = setOpen;
    setOpen = function (on) {
      if (!on) {
        if (pushTimer) { clearTimeout(pushTimer); pushTimer = null; }
        screens.forEach(function (s) { s.hidden = true; s.classList.remove('is-in'); });
        if (root) root.classList.remove('is-pushed');
      }
      baseSetOpen(on);
    };

    // Tapping a link navigates AND closes (37c). Focal's instant-navigation can serve a page
    // without a document swap, so the panel would otherwise stay open over the new page.
    panel.addEventListener('click', function (event) {
      if (event.target.closest('a')) setOpen(false);
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' || panel.hidden) return;
      // Esc unwinds ONE level: out of a pushed screen back to level 1, and only then out of the
      // menu. Closing the whole thing from inside a category would discard the buyer's position
      // for a key that everywhere else means « back one step ».
      if (screens.some(function (s) { return !s.hidden; })) popScreen();
      else setOpen(false);
    });
  }

  /* ── THE ACCOUNT DROPDOWN (CD S20 20b) ─────────────────────────────────────────────────────
     Lives here rather than in its own file because it is header chrome with the same lifecycle as
     the nav, and one script that owns the header's disclosures cannot get out of step with itself.

     🔴 CLICK, NOT HOVER — deliberately unlike the nav rail. An account menu carries « Se
     déconnecter »: opening it by passing the cursor over the control puts a destructive row under
     an accidental pointer. CD draws the popover's states, not its trigger, so this is DERIVED and
     recorded in DECISIONS.md. Closes on outside click, on Esc, and on focus leaving the wrap. */
  function initAccount() {
    var wrap = document.querySelector('[data-b2b-account]');
    if (!wrap) return;
    var panel = wrap.querySelector('[data-b2b-account-panel]');
    if (!panel) return;
    // TWO triggers, ONE panel — 20b: « l'icône compte ouvre ce menu TEL QUEL ». The desktop control
    // in the actions row and the 390 bar's account icon are the same disclosure in two layouts, so
    // they share a node rather than duplicating the block.
    var triggers = Array.prototype.slice.call(document.querySelectorAll('[data-b2b-account-trigger]'));
    if (!triggers.length) return;
    // 🔴 DESKTOP ONLY as of 2026-08-06. The mobile surface is no longer this node in a second box —
    // it is a real Focal `<drawer-content>` (see header.liquid), on the operator's ruling that the
    // mobile version be built on Focal's drawer rather than hand-rolled. Nothing below runs for it:
    // Focal's ToggleButton owns the trigger and OpenableElement owns append-body, the focus trap,
    // the scrim, the page lock and every close path.

    // 🔴 SAME CONTAINING-BLOCK TRAP AS THE MOBILE NAV, same fix. Measured at 390 after the nav was
    // moved: the sheet opened at y 0 with a height of ZERO, because it was still inside the sticky
    // <store-header>, which transforms itself on scroll — and a transformed ancestor becomes the
    // containing block for a `fixed` descendant, so `bottom: 0` resolved to the header's bottom.
    // Moving it to <body> costs the desktop popover its `absolute` anchor, so desktop is re-anchored
    // to the trigger's live rect below. That is strictly better anyway: the popover now follows the
    // control instead of the header box.
    if (panel.parentElement !== document.body) document.body.appendChild(panel);

    var DESKTOP_Q = '(min-width: 1000px)';

    /* 🔴 46g §8 — THE PANEL HANGS FROM THE HEADER LINE, NOT FROM THE ICON.
       This replaces the interim that shipped here (trigger bottom + 8), and CD names exactly what
       was wrong with it: « le panneau flottait entre deux appartenances. À 8 px sous l'icône, il
       pendait d'un bouton ; un menu de compte appartient au HEADER — comme la bottom-sheet
       appartient au bas d'écran. »

       The two axes have different owners, which is the whole ruling:
         · VERTICAL — the header. `top = header bottom edge + 10`, « quel que soit le contenu de la
           barre ». Read off `.b2bh`'s live rect so it survives the sticky header's own transform.
         · HORIZONTAL — the trigger. « son bord droit s'aligne sur le bord droit de la zone 44 de
           l'icône compte (pas sur la pilule panier : le panneau répond à l'icône qui l'a ouvert) ».

       DERIVED, and small: CD draws the account control as a bare 44×44 icon; this header ships it
       as a named button (company name + chevron). « The right edge of the icon's 44 zone » maps to
       the right edge of THAT control — the one that opened the panel, which is the clause's own
       stated reason. So the horizontal anchor is unchanged from the interim and only the vertical
       moves. Recorded because the frame's element and ours are not the same shape. */
    function placeDesktop() {
      if (!window.matchMedia(DESKTOP_Q).matches) {
        panel.style.top = '';
        panel.style.left = '';
        panel.style.right = '';
        return;
      }
      var t = triggers.filter(function (x) { return x.offsetParent !== null; })[0] || triggers[0];
      var r = t.getBoundingClientRect();
      var header = document.querySelector('.b2bh');
      // Fall back to the trigger's own line if the header bar is not on this page, so the panel
      // still lands somewhere sane rather than at y 0.
      var line = header ? header.getBoundingClientRect().bottom : r.bottom;
      panel.style.top = Math.round(line + 10) + 'px';
      panel.style.left = 'auto';
      panel.style.right = Math.round(window.innerWidth - r.right) + 'px';
    }

    /* 46g's entry/exit. `hidden` is `display: none`, which cancels transitions outright, so the
       panel is unhidden FIRST, placed, forced to lay out, and only then given `.is-open`. Closing
       runs the reverse and hides after the 120ms exit — tracked by a timer any re-open clears, or
       a fast toggle would hide a panel that had just been asked to open. */
    var closeTimer = null;

    function setOpen(on) {
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
      triggers.forEach(function (t) { t.setAttribute('aria-expanded', on ? 'true' : 'false'); });

      if (on) {
        panel.hidden = false;
        placeDesktop();
        void panel.offsetWidth;      // lay out at the closed state before transitioning out of it
        panel.classList.add('is-open');
      } else {
        panel.classList.remove('is-open');
        closeTimer = setTimeout(function () { panel.hidden = true; closeTimer = null; }, 120);
      }
      // No page lock: this is a desktop popover and 46g is explicit that there is « pas de scrim ».
      // The mobile sheet's lock is Focal's `.lock-all`, applied by DrawerContent itself.
    }

    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function (event) {
        event.stopPropagation();
        // The class, not `hidden`: during the 120ms exit the panel is still unhidden, so reading
        // `hidden` would treat a closing panel as closed and re-open it on the click that closed it.
        setOpen(!panel.classList.contains('is-open'));
      });
    });
    document.addEventListener('click', function (event) {
      if (!panel.classList.contains('is-open')) return;
      if (panel.contains(event.target)) return;
      if (triggers.some(function (t) { return t.contains(event.target); })) return;
      setOpen(false);
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && panel.classList.contains('is-open')) { setOpen(false); triggers[0].focus(); }
    });
    // The trigger moves with the header (sticky, hide-on-scroll), so an open popover has to follow
    // it rather than stay where it was drawn.
    window.addEventListener('resize', function () { if (panel.classList.contains('is-open')) placeDesktop(); });
    window.addEventListener('scroll', function () { if (panel.classList.contains('is-open')) placeDesktop(); }, { passive: true });

    /* 46g — « Fermeture : clic hors · Échap · re-clic icône · NAVIGATION — focus rendu au
       déclencheur ». The first three were already here; navigation was not. Focal navigates without
       a full reload on internal links, so a panel left open would survive the page change it
       caused. The links live inside the panel, so one delegated listener covers them. */
    panel.addEventListener('click', function (event) {
      if (event.target.closest('a[href]')) setOpen(false);
    });
  }

  function boot() {
    var nav = document.querySelector('[data-b2bnav]');
    if (nav) initDesktop(nav);
    var mobile = document.querySelector('[data-b2bnavm]');
    if (mobile) initMobile(mobile);
    initAccount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
